/**
 * News captions have no title field — the layout derives one at render time.
 * Pure helpers for NewsItem (and, later, the news detail screen).
 */

/** Longest first line that still reads as a headline rather than a sentence
 *  that happens to fit. Matches the design exploration (Aug 2026). */
const MAX_HEADLINE_LENGTH = 80;

const URL_RE = /https?:\/\/[^\s]+/g;

export interface SplitCaption {
  headline?: string;
  body?: string;
}

/**
 * First line of the caption becomes a bold lede when it is short enough to
 * read as one and carries no URL; everything else stays body text. A long or
 * link-bearing first line degrades gracefully to all-body — never force a
 * headline out of prose.
 */
export function splitNewsCaption(caption: string): SplitCaption {
  const trimmed = caption.trim();
  if (!trimmed) return {};
  const newline = trimmed.indexOf('\n');
  const first = (newline === -1 ? trimmed : trimmed.slice(0, newline)).trim();
  const rest = newline === -1 ? '' : trimmed.slice(newline + 1).trim();
  if (first.length > 0 && first.length <= MAX_HEADLINE_LENGTH && !URL_RE.test(first)) {
    // The RegExp has /g state; reset so the next call starts clean.
    URL_RE.lastIndex = 0;
    return { headline: first, body: rest || undefined };
  }
  URL_RE.lastIndex = 0;
  return { body: trimmed };
}

export type CaptionSegment =
  | { type: 'text'; text: string }
  | { type: 'link'; text: string; url: string };

/**
 * Split text into plain and link segments so the renderer can make URLs
 * tappable. Trailing sentence punctuation is peeled off the URL and returned
 * to the surrounding text ("see firesafenow.org/news." must not link the
 * final period).
 */
export function linkifyCaption(text: string): CaptionSegment[] {
  const segments: CaptionSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(URL_RE)) {
    let url = match[0];
    const start = match.index ?? 0;
    // Peel trailing punctuation (and an unmatched closing paren) off the URL.
    let trailing = '';
    for (;;) {
      const ch = url[url.length - 1];
      if ('.,;:!?\'"'.includes(ch)) {
        trailing = ch + trailing;
        url = url.slice(0, -1);
      } else if (ch === ')' && !url.includes('(')) {
        trailing = ch + trailing;
        url = url.slice(0, -1);
      } else {
        break;
      }
    }
    if (start > last) segments.push({ type: 'text', text: text.slice(last, start) });
    segments.push({ type: 'link', text: displayUrl(url), url });
    if (trailing) segments.push({ type: 'text', text: trailing });
    last = start + match[0].length;
  }
  if (last < text.length) segments.push({ type: 'text', text: text.slice(last) });
  return segments;
}

/** Strip protocol, www. and a trailing slash for reading; the full URL still
 *  opens on tap. */
export function displayUrl(url: string): string {
  return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
}

/** Runs of blank lines collapsed to one line break — a plan-update post's
 *  body often carries several, which made a 3-line feed-card clamp waste
 *  its lines on gaps instead of words (Sept 29 2026, F4). */
export function collapseBlankLines(text: string): string {
  return text.replace(/\n{2,}/g, '\n');
}
